/**
 * Finanzas Empresa API module.
 *
 * Covers all empresa-level finance search endpoints (arriendos, sueldos,
 * gastos, lavados, aseos, productos) and expense CRUD.
 * All endpoints require `administrador` role on the backend.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Common filter body for all empresa search endpoints. */
export interface EmpresaSearchBody {
  propiedades?: number[];
  bodegas?: number[];
  bodeguitas?: number[];
  from?: string;
  to?: string;
  [key: string]: any;
}

// ─── finanzasEmpresaApi ────────────────────────────────────────────────────────

export const finanzasEmpresaApi = {
  // ── Search endpoints ──────────────────────────────────────────────────────

  /** POST /finanzas/empresa/arriendos/search */
  searchArriendos: (body: EmpresaSearchBody): Promise<any | null> =>
    apiFetch("/finanzas/empresa/arriendos/search", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener arriendos",
    }),

  /** POST /finanzas/empresa/sueldos/search */
  searchSueldos: (body: EmpresaSearchBody): Promise<any | null> =>
    apiFetch("/finanzas/empresa/sueldos/search", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener sueldos",
    }),

  /** POST /finanzas/empresa/gastos/search */
  searchGastos: (body: EmpresaSearchBody): Promise<any | null> =>
    apiFetch("/finanzas/empresa/gastos/search", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener gastos",
    }),

  /**
   * POST /finanzas/empresa/lavados/search — handler único.
   *
   * Si el body trae `filters.fields.lavanderia_id` (número o `{ eq: id }`),
   * filtra por esa lavandería del catálogo. Sin filter, retorna los
   * huérfanos (`lavanderia_id IS NULL`, data legacy fantasma).
   *
   * Body extra opcional (round 2026-05-12 iter 28):
   *   - `rolFilter: "paga" | "recibe_intermediario"` — particiona las
   *     filas por `rol_de_aloja_facil`. Lo usa la vista de la genérica
   *     externa en /lavanderia para bifurcar sub-tabs "Costo real"
   *     (rol='paga') vs "Presupuesto estimado" (recibe + intermediario).
   *     Se aplica a nivel body (NO dentro de filters.fields) porque el
   *     backend lo traduce a literal SQL después de buildWhereFromFields.
   *
   * Response: siempre incluye totales agregados del período completo
   * (no de la página actual):
   *   - `totalMonto`, `totalExtraMonto`, `totalGeneral` — sumas crudas.
   *   - `totalIngresoEstimado` (iter 28) — SUM(monto+extra_monto) WHERE
   *     rol IN ('recibe','intermediario'). Ingresos esperados por la
   *     lavandería en el período.
   *   - `totalCostoReal` (iter 28) — SUM(monto+extra_monto) WHERE
   *     rol='paga'. Lo que AF realmente pagó al proveedor (genérica) o
   *     gastos manuales adicionales (AF interna).
   */
  searchLavados: (body: EmpresaSearchBody): Promise<any | null> =>
    apiFetch("/finanzas/empresa/lavados/search", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener lavados",
    }),

  /**
   * GET /finanzas/empresa/lavanderias — lista de lavanderías.
   *
   * Incluye `propiedades_count` y `lavados_count` calculados por subquery en
   * el backend, usados por la UI de `/lavanderia` para mostrar el impacto de
   * cada lavandería sin tener que pedir las propiedades una por una.
   * `es_generica` marca la fila no eliminable del sistema ("Lavandería externa").
   */
  getLavanderias: (): Promise<{
    data: Array<{
      id: number;
      nombre: string;
      direccion?: string;
      descripcion?: string;
      activo: boolean;
      es_generica?: boolean;
      propiedades_count?: number;
      lavados_count?: number;
      gastos_count?: number;
    }>;
  } | null> =>
    apiFetch("/finanzas/empresa/lavanderias", {
      errorMsg: "Error al obtener lavanderías",
    }),

  /**
   * GET /finanzas/empresa/lavanderias/:id/propiedades — listado de propiedades
   * asociadas a una lavandería. Lo usamos para:
   *   1. Expandir la fila en `LavanderiasTab` y ver qué propiedades dependen.
   *   2. Mostrar la lista de afectadas en el modal de eliminación.
   */
  getLavanderiaPropiedades: (
    id: number,
  ): Promise<{
    data: Array<{
      id: number;
      nombrepropiedad: string;
      habilitado: boolean;
      id_cliente: number | null;
      cliente: string | null;
    }>;
    lavanderia: { id: number; nombre: string };
  } | null> =>
    apiFetch(`/finanzas/empresa/lavanderias/${id}/propiedades`, {
      errorMsg: "Error al obtener propiedades de la lavandería",
    }),

  createLavanderia: (body: { nombre: string; direccion?: string; descripcion?: string }) =>
    apiFetch("/finanzas/empresa/lavanderias", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Lavandería creada",
    }),

  updateLavanderia: (id: number, body: { nombre?: string; direccion?: string; descripcion?: string; activo?: boolean }) =>
    apiFetch(`/finanzas/empresa/lavanderias/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Lavandería actualizada",
    }),

  /**
   * DELETE /finanzas/empresa/lavanderias/:id
   *
   * Si la lavandería tiene dependencias (propiedades, lavados o gastos), el
   * backend exige `body.reasignar_a` con el id de la destino. Si no hay
   * dependencias, el body es opcional. Si se omite el destino y hay
   * dependencias, el backend devuelve 409 con `code: "REASSIGN_REQUIRED"` y
   * los conteos en `deps`. Como `apiFetch` no lanza, usamos `silent` para no
   * mostrar el error y manejarlo en la UI con el modal.
   */
  deleteLavanderia: (
    id: number,
    body?: { reasignar_a?: number | null },
  ): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/lavanderias/${id}`, {
      method: "DELETE",
      body: body ? JSON.stringify(body) : undefined,
      // No mostramos toast automático: la UI decide qué mensaje mostrar
      // (success con detalle de reasignación, o conflicto con modal).
    }),

  /** POST /finanzas/empresa/aseos/search */
  searchAseos: (body: EmpresaSearchBody): Promise<any | null> =>
    apiFetch("/finanzas/empresa/aseos/search", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener aseos de empresa",
    }),

  /** POST /finanzas/empresa/productos/search */
  searchProductos: (body: EmpresaSearchBody): Promise<any | null> =>
    apiFetch("/finanzas/empresa/productos/search", {
      method: "POST",
      body: JSON.stringify(body),
      errorMsg: "Error al obtener productos",
    }),

  // ── Expense CRUD ──────────────────────────────────────────────────────────

  /** POST /finanzas/empresa/expenses */
  createExpense: (body: Record<string, any>): Promise<any | null> =>
    apiFetch("/finanzas/empresa/expenses", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Gasto creado correctamente",
      errorMsg: "Error al crear el gasto",
    }),

  /** PUT /finanzas/empresa/expenses/:id */
  updateExpense: (id: number, body: Record<string, any>): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/expenses/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Gasto actualizado correctamente",
      errorMsg: "Error al actualizar el gasto",
    }),

  /** DELETE /finanzas/empresa/expenses/:id */
  deleteExpense: (id: number): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/expenses/${id}`, {
      method: "DELETE",
      successMsg: "Gasto eliminado correctamente",
      errorMsg: "Error al eliminar el gasto",
    }),

  // ── Lavados (tabla unificada) CRUD ───────────────────────────────────────
  // Round 2026-05-11 iter 17: las antiguas tablas `lavanderia` /
  // `lavanderia_af` se unificaron en `lavados`. Estos endpoints sirven
  // CUALQUIER lavado del catálogo (AF interna o genérica externa) —
  // antes había dispatch por tabla origen, ya no aplica.

  /** POST /finanzas/empresa/lavados */
  createLavado: (body: Record<string, any>): Promise<any | null> =>
    apiFetch("/finanzas/empresa/lavados", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Lavado creado correctamente",
      errorMsg: "Error al crear el lavado",
    }),

  /**
   * PUT /finanzas/empresa/lavados/:id
   *
   * Round 2026-05-12 iter 37: si el record tiene `booking_id != null`,
   * el backend rechaza con 409 LAVADO_LOCKED_BY_BOOKING salvo que
   * `body.confirmDesync === true`. El frontend debe haber mostrado un
   * Modal.confirm con advertencia rica antes de setear ese flag — la
   * edición rompe la coherencia entre limpieza_clp (que ya entró como
   * ingreso al crear el arriendo) y el lavado AUTO (que la cancela
   * vía intermediario o la registra como estimado).
   */
  updateLavado: (id: number, body: Record<string, any>): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/lavados/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
      successMsg: "Lavado actualizado correctamente",
      errorMsg: "Error al actualizar el lavado",
    }),

  /**
   * DELETE /finanzas/empresa/lavados/:id
   *
   * Round 2026-05-12 iter 37: para lavados con `booking_id != null` se
   * envía `confirmDesync=true` en el body. Sin él, backend devuelve 409.
   */
  deleteLavado: (id: number, opts?: { confirmDesync?: boolean }): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/lavados/${id}`, {
      method: "DELETE",
      body: opts?.confirmDesync ? JSON.stringify({ confirmDesync: true }) : undefined,
      successMsg: "Lavado eliminado correctamente",
      errorMsg: "Error al eliminar el lavado",
    }),

  /**
   * PATCH /finanzas/empresa/lavados/:id/lavanderia
   *
   * Asigna o limpia el `lavanderia_id` de un lavado. Pasa
   * `lavanderia_id: null` para desasignar. Round 2026-05-12 iter 37:
   * para lavados con `booking_id != null` se envía `confirmDesync=true`
   * en el body (antes estaba bloqueado completamente; ver iter 37 doc).
   */
  setLavadoLavanderia: (
    id: number,
    lavanderia_id: number | null,
    opts?: { confirmDesync?: boolean },
  ): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/lavados/${id}/lavanderia`, {
      method: "PATCH",
      body: JSON.stringify({
        lavanderia_id,
        ...(opts?.confirmDesync ? { confirmDesync: true } : {}),
      }),
      successMsg:
        lavanderia_id == null
          ? "Lavado desasignado del catálogo"
          : "Lavandería asignada",
      errorMsg: "Error al reasignar el lavado",
    }),

  // ── Gastos operacionales lavandería AF ───────────────────────────────────

  /** GET /finanzas/empresa/lavanderia-gastos */
  getGastosOperacionales: (
    lavanderia_id?: number,
    periodo?: string,
  ): Promise<any | null> => {
    const params = new URLSearchParams();
    if (lavanderia_id) params.set("lavanderia_id", String(lavanderia_id));
    if (periodo) params.set("periodo", periodo);
    const qs = params.toString();
    return apiFetch(
      `/finanzas/empresa/lavanderia-gastos${qs ? `?${qs}` : ""}`,
      { errorMsg: "Error al obtener gastos operacionales" },
    );
  },

  /** POST /finanzas/empresa/lavanderia-gastos (upsert) */
  upsertGastosOperacionales: (body: Record<string, any>): Promise<any | null> =>
    apiFetch("/finanzas/empresa/lavanderia-gastos", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Gastos guardados correctamente",
      errorMsg: "Error al guardar los gastos",
    }),

  /** DELETE /finanzas/empresa/lavanderia-gastos/:id */
  deleteGastosOperacionales: (id: number): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/lavanderia-gastos/${id}`, {
      method: "DELETE",
      successMsg: "Gastos eliminados correctamente",
      errorMsg: "Error al eliminar los gastos",
    }),

  // ── Publicación de arriendos al Cliente (visible_cliente) ─────────────────
  // Ver docs/flujo-financiero.md §13.

  /** PATCH /finanzas/empresa/arriendos/:id/publicar — marca visible_cliente=true */
  publicarArriendo: (id: number): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/arriendos/${id}/publicar`, {
      method: "PATCH",
      successMsg: "Arriendo publicado — el cliente lo ve ahora",
      errorMsg: "Error al publicar arriendo",
    }),

  /** PATCH /finanzas/empresa/arriendos/:id/despublicar — marca visible_cliente=false */
  despublicarArriendo: (id: number): Promise<any | null> =>
    apiFetch(`/finanzas/empresa/arriendos/${id}/despublicar`, {
      method: "PATCH",
      successMsg: "Arriendo despublicado",
      errorMsg: "Error al despublicar arriendo",
    }),

  /**
   * POST /finanzas/empresa/arriendos/publicar-bulk
   * body: { visible: boolean, mes?: 'YYYY-MM', idPropietario?: number, idPropiedad?: number }
   */
  publicarArriendosBulk: (body: {
    visible: boolean;
    mes?: string;
    idPropietario?: number;
    idPropiedad?: number;
  }): Promise<any | null> =>
    apiFetch("/finanzas/empresa/arriendos/publicar-bulk", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: "Publicación bulk aplicada",
      errorMsg: "Error en publicación bulk",
    }),

  // ── Cierre mensual unificado por (propiedad, mes) ─────────────────────────
  // Compuerta ÚNICA de visibilidad al cliente: publica/oculta el mes completo
  // (arriendos + gastos + aseos + productos + lavandería). Ver flujo-financiero.md §13.

  /** GET /finanzas/empresa/cierres?mes=YYYY-MM — estado explícito por propiedad. */
  getCierresMes: (mes: string): Promise<{ success: boolean; mes: string; rows: any[] } | null> =>
    apiFetch(`/finanzas/empresa/cierres?mes=${encodeURIComponent(mes)}`, {
      errorMsg: "Error al cargar el estado de cierres",
    }),

  /**
   * POST /finanzas/empresa/cierre — publica/oculta el mes COMPLETO al cliente.
   * body: { publicado, mes: 'YYYY-MM', idPropietario?, idPropiedad? }.
   * Sin idPropietario/idPropiedad aplica a TODAS las propiedades.
   */
  setCierreMensual: (body: {
    publicado: boolean;
    mes: string;
    idPropietario?: number;
    idPropiedad?: number;
  }): Promise<any | null> =>
    apiFetch("/finanzas/empresa/cierre", {
      method: "POST",
      body: JSON.stringify(body),
      successMsg: body.publicado
        ? "Mes publicado al cliente"
        : "Mes ocultado al cliente",
      errorMsg: "Error al actualizar el cierre del mes",
    }),
};
